import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Keyboard } from 'lucide-react';
import { cn } from '../lib/utils';
import type { ShortcutGroup, ShortcutHelpSection } from '../hooks/use-editor-shortcuts';

// ─────────────────────────────────────────────────────────────────────────────
// Single, app-level keyboard-shortcuts overlay. It is purely presentational and
// data-driven: it renders whatever grouped `sections` it is handed (the app's
// base bindings PLUS the active editor's context bindings, which editors
// publish to the shortcuts store). Same component, both editors — the docs
// can never drift from behaviour because they come from the same registry.
// Closes on Esc / backdrop click.
// ─────────────────────────────────────────────────────────────────────────────

interface ShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
  sections: ShortcutHelpSection[];
  title?: string;
}

function groupLabel(t: ReturnType<typeof useTranslation>['t'], group: ShortcutGroup): string {
  switch (group) {
    case 'create': return t('shortcuts.groupCreate', 'Criação');
    case 'navigate': return t('shortcuts.groupNavigate', 'Navegação');
    case 'edit': return t('shortcuts.groupEdit', 'Edição');
    case 'global': return t('shortcuts.groupGlobal', 'Global');
    case 'help': return t('shortcuts.groupHelp', 'Ajuda');
    default: return group;
  }
}

function KeyCap({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[28px] h-7 items-center justify-center rounded-md border border-border bg-card px-2 text-[11px] font-mono font-semibold leading-none text-foreground shadow-[0_1px_0_0_var(--border)] select-none">
      {children}
    </kbd>
  );
}

export function ShortcutsHelp({ open, onClose, sections, title }: ShortcutsHelpProps) {
  const { t } = useTranslation();

  // Esc always closes — owned here so it works regardless of anything else.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — centred modal on desktop, bottom sheet on mobile */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title ?? t('shortcuts.title', 'Atalhos de teclado')}
        className={cn(
          'fixed z-[1000] flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl',
          'inset-x-4 bottom-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
          'w-auto sm:w-full sm:max-w-2xl',
          'max-h-[85vh]',
        )}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Keyboard size={17} />
            </span>
            <div>
              <span className="block text-base font-semibold text-foreground">
                {title ?? t('shortcuts.title', 'Atalhos de teclado')}
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {t('shortcuts.subtitle', 'Pressione ? para abrir · Esc para fechar')}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('common.close', 'Fechar')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6">
          <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            {sections.map((section, si) => (
              <section key={`${section.group}-${si}`} className="min-w-0">
                <h3 className="mb-3 border-b border-border/60 pb-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-primary">
                  {section.title ?? groupLabel(t, section.group)}
                </h3>
                <ul className="space-y-2.5">
                  {section.items.map((item) => (
                    <li key={item.id} className="group flex items-center justify-between gap-4">
                      <span className="min-w-0 flex-1 text-sm leading-tight text-muted-foreground transition-colors group-hover:text-foreground">
                        {item.description}
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        {item.combos.map((combo, ci) => (
                          <span key={ci} className="flex items-center gap-1">
                            {ci > 0 && (
                              <span className="px-0.5 text-[10px] text-muted-foreground">
                                {t('shortcuts.or', 'ou')}
                              </span>
                            )}
                            {combo.map((part, pi) => (
                              <span key={pi} className="flex items-center gap-1">
                                {pi > 0 && <span className="text-[10px] text-muted-foreground">+</span>}
                                <KeyCap>{part}</KeyCap>
                              </span>
                            ))}
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Ghost "?" toolbar button — opens the app-level overlay via a window event
 * so it can be dropped into any editor toolbar without prop-drilling state. */
export function ShortcutsHelpButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent('reformu:shortcuts-open'))}
      title={t('shortcuts.buttonTitle', 'Atalhos de teclado (?)')}
      aria-label={t('shortcuts.buttonTitle', 'Atalhos de teclado')}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <Keyboard size={17} />
    </button>
  );
}
